// Every WhatsApp button on the booking domains goes through here: a plain
// wa.me link to the market's business number with a prefilled message, plus
// a data-wa-source value. The GTM tag "WhatsApp – Ref code + click ID" reads
// data-wa-source and appends " Ref: XXXXXX" to the message itself — so we
// never add a Ref here, and the link must stay a plain wa.me link.
import { market } from "@/utils/market";
import { pageContext } from "@/utils/pageContext";
import { track } from "@/utils/tracking";

// data-wa-source → prefilled message. `s` is the short suburb ("Killara").
// Sources not in the brief's table: booking_link_questions (personal
// booking/manage pages), photo_quote_questions (photo-quote side card),
// photo_quote_header — tell Logan before GTM relies on them.
const MESSAGES = {
  booking_header: ({ s }) =>
    s
      ? `Hi! I'm booking a drop-off at ${s} and have a question.`
      : "Hi! I'm booking a drop-off and have a question.",
  booking_step1_questions: ({ s }) =>
    s
      ? `Hi! I have a question before I book a drop-off at ${s}.`
      : "Hi! I have a question before I book a drop-off.",
  booking_step2_no_time: ({ s }) =>
    s
      ? `Hi! I'd like to book a drop-off at ${s} but can't find a time that suits me.`
      : "Hi! I'd like to book a drop-off but can't find a time that suits me.",
  booking_step2_late: ({ s }) =>
    s ? `Hi! I'm booked at ${s} and running late.` : "Hi! I'm booked for a drop-off and running late.",
  booking_step3_questions: ({ s }) =>
    s
      ? `Hi! I have a question about my ${s} drop-off before I confirm.`
      : "Hi! I have a question about my drop-off before I confirm.",
  booking_thankyou: ({ s, ref }) =>
    `Hi! I've just booked a drop-off${s ? ` at ${s}` : ""}${ref ? ` (booking ref ${ref})` : ""} and have a question.`,
  booking_thankyou_updates: ({ s, ref, name, when }) =>
    `Hi, I'm ${name || "booking"}. I've just booked drop-off${ref ? ` ${ref}` : ""}` +
    `${s ? ` at ${s}` : ""}${when ? ` on ${when}` : ""}. Please send updates here.`,
  booking_link_questions: ({ s }) =>
    s
      ? `Hi! I have a question about my drop-off at ${s}.`
      : "Hi! I have a question about my drop-off.",
  photo_quote_header: () => "Hi! I have a question about getting a quote for my tapes.",
  photo_quote_whatsapp: () => "Hi! I'd like a quote. Here's a photo of my tapes.",
  photo_quote_questions: () => "Hi! I have a question about my quote.",
  order_form_done: ({ s, when, branch, name }) =>
    branch === "post"
      ? `Hi, I'm ${name || "a customer"}. I've asked for a quote and I'm posting my tapes to you. Please send updates here.`
      : `Hi, I'm ${name || "a customer"}. I've asked for a quote and booked a drop-off` +
        `${s ? ` at ${s}` : ""}${when ? ` on ${when}` : ""}. Please send updates here.`,
};

export function whatsappMessage(source, vars = {}) {
  const build = MESSAGES[source] || MESSAGES.booking_header;
  return build({ s: vars.suburb, ...vars });
}

export function whatsappHref(source, vars = {}) {
  return `https://wa.me/${market.whatsapp}?text=${encodeURIComponent(whatsappMessage(source, vars))}`;
}

// GA4 whatsapp_click — no personal details
export function trackWhatsappClick(source, { step, location } = {}) {
  track({
    event: "whatsapp_click",
    wa_source: source,
    booking_step: step !== undefined ? step : pageContext.step,
    booking_location: location !== undefined ? location : pageContext.location,
  });
}
